// @givmo/app-charity — two-factor code prompt (AS-WEB1).
//
// A charity admin who is also a Givmo consumer may have enrolled TOTP on the mobile app (web ships no
// enrolment). Their password/OAuth first factor then succeeds and GCIP demands a second factor, which
// @givmo/auth hands back as a `TotpChallenge` on the thrown error. This renders that challenge as a
// real STATE of the sign-in screen — the form is replaced in place — and there is always a way back
// to it, because an enrolled admin must never be stranded out of the portal.
//
// SINGLE-USE CODES. A TOTP code is valid once per 30-second window; a spent code is rejected and only
// the next window's code works. So a rejection CLEARS the field rather than leaving the code sitting
// there for a second submit, and re-typing an already-spent code keeps Verify disabled — the retry is
// structurally forced to be a fresh code, not a resubmission of the failed one.
//
// Mirrors the admin app's prompt (the two apps duplicate their auth screens by convention), styled
// against the charity portal's own auth surface and its free-text code entry.
//
// It is no longer sign-in-only: the payout step-up dialog (AS-WEB3) reuses it to complete a challenge
// raised mid-re-authentication. Hence the copy overrides — every default string here narrates
// SIGNING IN, and a signed-in Owner authorising a bank change is not signing in.

import { useCallback, useState, type ReactNode } from 'react';
import { Button, TextField, ErrorMessage, testIdProp } from '@givmo/design-system';
import { SignInDoorError, type SignInDoorErrorProps } from '@givmo/passkeys';
import type { TotpChallenge } from '@givmo/auth';
import type { UserCredential } from 'firebase/auth';
import { useAuthError } from './use-auth-error';

const CODE_LENGTH = 6;
const CODE_REJECTED_MESSAGE =
  'That code was already used or has expired — enter the current code from your authenticator app.';
const CODE_ALREADY_TRIED_MESSAGE =
  'You already used that code. Wait for your authenticator app to show the next one.';

export interface TotpChallengePromptProps {
  /** The pending challenge, read off the caught sign-in failure with `secondFactorChallengeOf`. */
  challenge: TotpChallenge<UserCredential>;
  /** Called once the code is accepted and the session is live. */
  onResolved: () => void | Promise<void>;
  /** Abandon the challenge and return to whatever raised it. */
  onCancel: () => void;
  /**
   * Label for the escape hatch. The sign-in screen returns to its form ("Back to sign in"); the
   * payout step-up dialog returns to its own verify prompt, where that wording would be a lie.
   */
  cancelLabel?: string;
  /**
   * Heading override. The defaults below say the code FINISHES SIGNING IN, which is true on the
   * sign-in screen and false everywhere else this prompt is now reused: a step-up challenge
   * interrupts an already-signed-in session mid-action (a bank change), and telling that user they
   * are signing in misdescribes what their code is about to authorise. A surface that is not a
   * sign-in must override both.
   */
  title?: string;
  /**
   * Body override. Receives the enrolled factor's display name when the user set one, so a caller
   * can keep the "code from <app>" specificity while changing what the code is FOR.
   */
  description?: (factorDisplayName: string | null) => string;
  /**
   * Opens the way back in (AS-WEB27): the company's sign-in lock can refuse a sign-in as its code
   * completes it, and then the prompt shows the lock notice. Absent (a step-up), the refusal shows
   * as its plain line.
   */
  onUnlink?: () => void;
  /** The lock notice's organization door, where the page offers one (AS-WEB30). */
  organizationSso?: SignInDoorErrorProps['organizationSso'];
  /**
   * Board's one line for the lock notice, on its director door from an invitation (AS-WEB27's rider;
   * cut 1007e's fold, Board's B-F2): the lock can refuse as the code completes the sign-in.
   */
  lockNoticeLine?: ReactNode;
}

/** Default body copy — accurate only where the challenge really is completing a sign-in. */
function signInDescription(factorDisplayName: string | null): string {
  return factorDisplayName !== null
    ? `Enter the six-digit code from ${factorDisplayName} to finish signing in.`
    : 'Enter the six-digit code from your authenticator app to finish signing in.';
}

export function TotpChallengePrompt({
  challenge,
  onResolved,
  onCancel,
  cancelLabel = 'Back to sign in',
  title = 'Two-factor authentication',
  description = signInDescription,
  onUnlink,
  organizationSso,
  lockNoticeLine,
}: TotpChallengePromptProps) {
  const { error, lockRefused, clearError, surface } = useAuthError();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rejected, setRejected] = useState(false);
  // Codes this challenge has already spent. State, not a ref: it is read during render to gate the
  // Verify button, and a ref read during render is not safe under concurrent rendering.
  const [spentCodes, setSpentCodes] = useState<ReadonlySet<string>>(() => new Set());

  const trimmed = code.trim();
  const alreadyTried = spentCodes.has(trimmed);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (trimmed.length !== CODE_LENGTH || submitting || alreadyTried) return;
      clearError();
      setRejected(false);
      setSubmitting(true);
      try {
        await challenge.resolve(trimmed);
        await onResolved();
      } catch (thrown) {
        // Spend the code and clear the field: the next attempt cannot be this same code.
        setSpentCodes((spent) => new Set(spent).add(trimmed));
        setCode('');
        const authError = surface(thrown);
        if (authError.kind === 'invalidVerificationCode') setRejected(true);
      } finally {
        setSubmitting(false);
      }
    },
    [trimmed, submitting, alreadyTried, clearError, challenge, onResolved, surface],
  );

  return (
    <div className="flex flex-col gap-20" {...testIdProp('auth-totp-challenge')}>
      <div className="flex flex-col gap-4">
        <h1 className="font-sans text-s22 font-w700 tracking-givmo text-text-strong">{title}</h1>
        <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
          {description(challenge.factorDisplayName)}
        </p>
      </div>

      <form className="flex flex-col gap-16" onSubmit={(e) => void onSubmit(e)} noValidate>
        <TextField
          label="Authentication code"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          testId="auth-totp-code"
        />

        {rejected && <ErrorMessage testId="auth-totp-error">{CODE_REJECTED_MESSAGE}</ErrorMessage>}
        {alreadyTried && (
          <ErrorMessage testId="auth-totp-reused">{CODE_ALREADY_TRIED_MESSAGE}</ErrorMessage>
        )}
        {/* Anything that isn't a rejected code — an expired challenge, a network failure — shows its
            own message; "Back to sign in" is the way out of every one of them. */}
        {!rejected && (
          <SignInDoorError
            error={error}
            lockRefused={lockRefused}
            onUnlink={onUnlink}
            organizationSso={organizationSso}
            line={lockNoticeLine}
          />
        )}

        <Button
          type="submit"
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          loading={submitting}
          disabled={trimmed.length !== CODE_LENGTH || alreadyTried}
          testId="auth-totp-verify"
        >
          Verify
        </Button>
      </form>

      <button
        type="button"
        onClick={onCancel}
        className="self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
        {...testIdProp('auth-totp-cancel')}
      >
        {cancelLabel}
      </button>
    </div>
  );
}
